import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Invoices } from '@components/my-account/billing-and-subscription/invoices.tsx';
import { Subscriptions } from '@components/my-account/billing-and-subscription/subscriptions.tsx';
import { Payment } from '@components/my-account/billing-and-subscription/payment.tsx';
import { usePageHeader } from '@utils/hooks/use-page-header';
import { useTranslation } from 'react-i18next';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import { Package, CreditCard, FileText } from 'lucide-react';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { PATHS } from '../../router/paths.ts';

export enum BillingAndSubscriptionTabs {
  Payments = 'Payments',
  Subscriptions = 'Subscriptions',
  Invoices = 'Invoices'
}

export default function BillingAndSubscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: organization } = useOrganization();
  const { data: me } = useGetMe();

  // Check if user has admin or sysop role
  const userRoles = (me?.result?.roles ?? []) as unknown as string[];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('sysop');

  // Redirect non-admin users to home
  useEffect(() => {
    if (me && !isAdmin) {
      navigate(PATHS().HOME);
    }
  }, [me, isAdmin, navigate]);

  const isResellee = Boolean(organization?.is_resellee);
  const showPayments = !isResellee;
  const showAnyInvoices = !isResellee;

  const billingAndSubscriptionTabs: Tab[] = [
    {
      id: 0,
      name: (
        <span className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          {t('subscriptions')}
        </span>
      ),
      component: <Subscriptions />
    },
    ...(showPayments
      ? [
          {
            id: 1,
            name: (
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                {t('payments')}
              </span>
            ),
            component: <Payment />
          } as Tab
        ]
      : []),
    ...(showAnyInvoices
      ? [
          {
            id: 2,
            name: (
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('invoices')}
              </span>
            ),
            component: <Invoices />
          } as Tab
        ]
      : [])
  ];

  const caption =
    showPayments || showAnyInvoices
      ? t('a-list-of-all-the-invoices-subscriptions-and-payment-methods')
      : t('a-list-of-all-the-subscriptions');

  usePageHeader({
    title: t('invoices-and-subscriptions'),
    description: caption
  });

  return (
    <div className="bg-app min-h-screen space-y-2">
      <Tabs tabs={billingAndSubscriptionTabs} searchParams minHeight={'84vh'} />
    </div>
  );
}
